// src/components/QRScanModal.js
// 平板相機 QR code 掃描器 + 操作員選擇 + 快速入庫
// 支援兩種 QR 格式：
//   YPFOPS:pid:{product_id}  → 貨架 QR，手動填數量
//   YPFOPS:unit:{label_id}   → 包裝標籤 QR，數量自動帶入，防重複入庫
import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { C, st } from '../theme'
import { Html5Qrcode } from 'html5-qrcode'

const SCAN_ELEMENT_ID = 'ypfops-qr-scanner'

export default function QRScanModal({ userProfile, products, onRefresh, addLog, showToast, onClose, initialScan }) {
  // 若有 initialScan（手機相機掃碼帶進來），直接跳過相機步驟
  const hasInitialScan = !!(initialScan?.pid || initialScan?.lid)
  const [step, setStep] = useState(hasInitialScan ? 'loading' : 'scan')
  const [scannedProduct, setScannedProduct] = useState(null)
  const [scannedLabel, setScannedLabel] = useState(null)  // package_label 記錄（若為包裝 QR）
  const [operator, setOperator] = useState(null)
  const [qty, setQty] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [camError, setCamError] = useState(null)
  const [companyUsers, setCompanyUsers] = useState([])
  const [scannerReady, setScannerReady] = useState(false)
  const [pendingQR, setPendingQR] = useState(null)  // 相機掃到的原始文字，交給 useEffect 處理
  const html5QrRef = useRef(null)
  const scanLockRef = useRef(false)      // 防止 Html5Qrcode 10fps 重複觸發
  const productsRef = useRef(products)   // 永遠指向最新 products（修正 stale closure）
  useEffect(() => { productsRef.current = products }, [products])

  // 載入公司員工清單
  useEffect(() => {
    if (!userProfile?.company_id) return
    supabase.from('user_profiles')
      .select('id, name, position, role')
      .eq('company_id', userProfile.company_id)
      .eq('approved', true)
      .order('name')
      .then(({ data }) => { if (data) setCompanyUsers(data) })
  }, [userProfile])

  // 處理 initialScan（手機相機掃碼 → 帶著 pid/lid 進來，跳過相機步驟）
  useEffect(() => {
    if (!initialScan) return
    const { pid, lid } = initialScan

    const process = async () => {
      if (lid) {
        // 包裝標籤 QR
        const { data: label, error } = await supabase
          .from('package_labels')
          .select('*, products(name, sku, unit, stock)')
          .eq('id', lid)
          .single()
        if (error || !label) {
          showToast('找不到此包裝標籤，請確認正確', 'error')
          setStep('scan')
          return
        }
        if (label.status === 'checked_in') {
          setScannedLabel(label)
          setScannedProduct({ ...label.products, id: label.product_id })
          setStep('duplicate')
          return
        }
        setScannedLabel(label)
        setScannedProduct({ ...label.products, id: label.product_id })
        setQty('')
        setStep('operator')
      } else if (pid) {
        // 貨架商品 QR
        const product = products.find(p => p.id === pid)
        if (!product) {
          showToast('找不到此商品，請確認標籤正確', 'error')
          setStep('scan')
          return
        }
        setScannedLabel(null)
        setScannedProduct(product)
        setQty('')
        setStep('operator')
      }
    }

    process()
  }, []) // 只在 mount 時執行一次，initialScan 不會改變

  // 啟動相機掃描
  useEffect(() => {
    if (step !== 'scan') return
    let scanner = null
    const timer = setTimeout(async () => {
      try {
        scanner = new Html5Qrcode(SCAN_ELEMENT_ID)
        html5QrRef.current = scanner
        await scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 240, height: 240 }, aspectRatio: 1.0 },
          (decodedText) => {
            // callback 只設 state，讓 React useEffect 做實際處理（避免 callback 裡 async 不穩定）
            if (!scanLockRef.current) {
              scanLockRef.current = true
              setPendingQR(decodedText)
            }
          },
          () => {}
        )
        setScannerReady(true)
      } catch (e) {
        setCamError('無法存取相機，請確認已授權相機權限。\n\n' + e?.message)
      }
    }, 300)
    return () => {
      clearTimeout(timer)
      if (scanner) {
        try { scanner.stop().catch(() => {}) } catch (_) {}  // 防止 stop() 同步拋錯
      }
    }
  }, [step])

  const stopScanner = async () => {
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop() } catch (_) {}
      html5QrRef.current = null
    }
  }

  // ── 處理掃描結果（pendingQR 由相機 callback 設定，在 React 上下文裡安全執行）──
  useEffect(() => {
    if (!pendingQR) return
    const text = pendingQR

    const processQR = async () => {
      await stopScanner()

      // ── 格式 1：包裝標籤 QR → ?scan=unit&lid={label_id} 或舊格式 YPFOPS:unit: ──
      let unitLid = null
      if (text.startsWith('YPFOPS:unit:')) unitLid = text.replace('YPFOPS:unit:', '').trim()
      else if (text.includes('scan=unit')) { try { unitLid = new URL(text).searchParams.get('lid') } catch (_) {} }

      if (unitLid) {
        const { data: label, error } = await supabase
          .from('package_labels')
          .select('*, products(name, sku, unit, stock)')
          .eq('id', unitLid)
          .single()

        if (error || !label) {
          showToast('找不到此包裝標籤，請確認正確', 'error')
          setScannerReady(false); scanLockRef.current = false; setPendingQR(null); setStep('scan'); return
        }
        if (label.status === 'checked_in') {
          setScannedLabel(label)
          setScannedProduct({ ...label.products, id: label.product_id })
          setPendingQR(null); setStep('duplicate'); return
        }
        setScannedLabel(label)
        setScannedProduct({ ...label.products, id: label.product_id })
        setQty('')
        setPendingQR(null); setStep('operator'); return
      }

      // ── 格式 2：貨架 QR → ?scan=in&pid={product_id} 或舊格式 YPFOPS:pid: ──
      let pid = null
      if (text.startsWith('YPFOPS:pid:')) pid = text.replace('YPFOPS:pid:', '').trim()
      else if (text.includes('pid=')) {
        try { pid = new URL(text).searchParams.get('pid') } catch (_) {}
      }

      if (!pid) {
        showToast('無法識別此 QR code', 'error')
        setScannerReady(false); scanLockRef.current = false; setPendingQR(null); setStep('scan'); return
      }
      const product = productsRef.current.find(p => p.id === pid)
      if (!product) {
        showToast('找不到此商品，請確認標籤正確', 'error')
        setScannerReady(false); scanLockRef.current = false; setPendingQR(null); setStep('scan'); return
      }

      setScannedLabel(null)
      setScannedProduct(product)
      setQty('')
      setPendingQR(null); setStep('operator')
    }

    processQR()
  }, [pendingQR])

  const handleSelectOperator = (user) => {
    setOperator(user)
    setStep('qty')
  }

  const handleSubmit = async () => {
    const qtyNum = parseInt(qty)
    if (!qtyNum || qtyNum <= 0) { showToast('請輸入有效數量', 'error'); return }
    setSaving(true)
    try {
      const operatorName = operator?.name || userProfile?.name
      const recordNote = scannedLabel
        ? `QR包裝入庫 · 批次 ${scannedLabel.batch_no}${note ? ' · ' + note : ''}`
        : (note || 'QR 掃描入庫')

      await Promise.all([
        supabase.rpc('adjust_stock', { p_product_id: scannedProduct.id, p_delta: qtyNum }),
        supabase.from('records').insert({
          product_id: scannedProduct.id,
          type: 'in',
          qty: qtyNum,
          note: recordNote,
          operator: operatorName,
          company_id: userProfile?.company_id || null,
        }),
        // 包裝標籤：標記為已入庫
        ...(scannedLabel ? [
          supabase.from('package_labels').update({
            status: 'checked_in',
            checked_in_at: new Date().toISOString(),
            checked_in_by: operatorName,
          }).eq('id', scannedLabel.id)
        ] : []),
      ])

      const logType = scannedLabel ? 'QR包裝入庫' : 'QR掃描入庫'
      await addLog(logType, `${scannedProduct.name} +${qtyNum}（操作員：${operatorName}）`)
      showToast(`✓ ${scannedProduct.name} 入庫 ${qtyNum} ${scannedProduct.unit}`)
      onRefresh()
      // 重置，繼續掃下一個（解鎖 + 重置 scannerReady 讓相機正常重啟）
      setScannedProduct(null); setScannedLabel(null); setOperator(null); setQty(''); setNote('')
      setSaving(false); setScannerReady(false); scanLockRef.current = false; setPendingQR(null); setStep('scan')
    } catch (e) {
      showToast('入庫失敗：' + e.message, 'error')
      setSaving(false)
    }
  }

  const handleBack = async () => {
    if (step === 'qty') { setStep('operator'); return }
    if (step === 'operator' || step === 'duplicate') {
      setScannedProduct(null); setScannedLabel(null); setQty(''); setNote('')
      setScannerReady(false); scanLockRef.current = false; setPendingQR(null)
      setStep('scan'); return
    }
    await stopScanner()
    onClose()
  }

  return (
    <>
      {/* Backdrop — 手機不設 onClick，避免觸控誤觸關閉，改用 ← 按鈕 */}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 2000 }} />

      <div onClick={e => e.stopPropagation()} style={{
        position: 'fixed', inset: 0, zIndex: 2001,
        display: 'flex', flexDirection: 'column',
        background: C.bg, maxWidth: 480, margin: '0 auto',
      }}>
        {/* Header */}
        <div style={{ background: C.panel, borderBottom: `1.5px solid ${C.border}`, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <button onClick={handleBack} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.muted, padding: '0 4px' }}>←</button>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>
              {step === 'loading' ? '🔍 讀取掃描資料' : step === 'scan' ? '📷 掃描商品 QR code' : step === 'duplicate' ? '⚠️ 此標籤已入庫' : step === 'operator' ? '👤 請選擇操作員' : `📦 入庫 · ${scannedProduct?.name}`}
            </div>
            <div style={{ fontSize: 11, color: C.muted }}>
              {step === 'loading' ? '正在查詢商品資訊，請稍候...' : step === 'scan' ? '將鏡頭對準包裝或貨架上的 QR code 標籤' : step === 'duplicate' ? '此包裝已被入庫，無法重複處理' : step === 'operator' ? '選擇今天操作的人員' : `現有庫存：${scannedProduct?.stock} ${scannedProduct?.unit}`}
            </div>
          </div>
          <div style={{ fontSize: 11, color: C.muted, background: C.panel, border: `1px solid ${C.border}`, borderRadius: 20, padding: '4px 10px' }}>
            {step === 'scan' || step === 'loading' ? '1/3' : step === 'operator' ? '2/3' : '3/3'}
          </div>
        </div>

        {/* Step: 初始掃描載入中（來自手機相機）*/}
        {step === 'loading' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
            <div style={{ fontSize: 40 }}>🔍</div>
            <div style={{ fontSize: 14, color: C.muted }}>正在查詢商品資料...</div>
          </div>
        )}

        {/* Step: 重複入庫警告 */}
        {step === 'duplicate' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 28, gap: 16, textAlign: 'center' }}>
            <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>⚠️</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#dc2626' }}>此包裝已入庫</div>
            <div style={{ background: '#fef2f2', border: '1.5px solid #fecaca', borderRadius: 12, padding: '14px 18px', width: '100%' }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 8 }}>{scannedProduct?.name}</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>數量：{scannedLabel?.qty} {scannedProduct?.unit}</div>
              <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>批次：{scannedLabel?.batch_no}</div>
              {scannedLabel?.checked_in_by && <div style={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>入庫人員：{scannedLabel.checked_in_by}</div>}
              {scannedLabel?.checked_in_at && <div style={{ fontSize: 12, color: C.muted }}>入庫時間：{new Date(scannedLabel.checked_in_at).toLocaleString('zh-TW')}</div>}
            </div>
            <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.6 }}>此標籤已被入庫，<strong style={{ color: '#dc2626' }}>不會重複計入</strong>。<br/>如需修正，請至倉儲管理查詢紀錄。</div>
            <button onClick={handleBack} style={{ padding: '14px 32px', borderRadius: 12, background: C.accent, border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              ← 繼續掃描下一個
            </button>
          </div>
        )}

        {/* Step: 掃描 */}
        {step === 'scan' && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            {camError ? (
              <div style={{ textAlign: 'center', padding: 24 }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>📵</div>
                <div style={{ fontSize: 14, color: C.red, fontWeight: 700, marginBottom: 8 }}>相機無法使用</div>
                <div style={{ fontSize: 12, color: C.muted, whiteSpace: 'pre-line', lineHeight: 1.6 }}>{camError}</div>
              </div>
            ) : (
              <>
                <div id={SCAN_ELEMENT_ID} style={{ width: '100%', maxWidth: 340, minHeight: 260, borderRadius: 16, overflow: 'hidden', border: `3px solid ${C.accent}` }} />
                {!scannerReady && (
                  <div style={{ marginTop: 16, color: C.muted, fontSize: 13 }}>正在啟動相機...</div>
                )}
                <div style={{ marginTop: 20, fontSize: 13, color: C.muted, textAlign: 'center' }}>
                  對準貨架標籤上的 QR code<br />偵測到後自動跳下一步
                </div>
              </>
            )}
          </div>
        )}

        {/* Step: 選操作員 */}
        {step === 'operator' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            {companyUsers.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: C.muted }}>載入員工清單中...</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
                {companyUsers.map(u => (
                  <button key={u.id} onClick={() => handleSelectOperator(u)} style={{
                    padding: '20px 12px', borderRadius: 14, border: `2px solid ${C.border}`,
                    background: C.panel, cursor: 'pointer', textAlign: 'center',
                    transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = C.accent; e.currentTarget.style.background = C.accentDim }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = C.panel }}>
                    <div style={{ width: 48, height: 48, borderRadius: '50%', background: `linear-gradient(135deg,${C.accent},#0ea5e9)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 900, color: '#fff', margin: '0 auto 10px' }}>
                      {(u.name || '?')[0].toUpperCase()}
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{u.position || u.role || ''}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Step: 填數量 */}
        {step === 'qty' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16 }}>
            {/* 操作員 badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: C.accentDim, borderRadius: 12, padding: '10px 14px' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 900, color: '#fff', flexShrink: 0 }}>
                {(operator?.name || '?')[0].toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.accent }}>操作員：{operator?.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>此筆紀錄將記錄在您名下</div>
              </div>
            </div>

            {/* 商品資訊 */}
            <div style={{ background: C.panel, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: '14px 16px' }}>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 4 }}>商品</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: C.text }}>{scannedProduct?.name}</div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>SKU：{scannedProduct?.sku} · 現有庫存：<span style={{ color: C.accent, fontWeight: 700 }}>{scannedProduct?.stock} {scannedProduct?.unit}</span></div>
            </div>

            {/* 包裝標籤提示 */}
            {scannedLabel && (
              <div style={{ background: '#dcfce7', border: '1.5px solid #86efac', borderRadius: 10, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 18 }}>🏷️</span>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d' }}>包裝標籤入庫 · 數量已自動帶入</div>
                  <div style={{ fontSize: 11, color: '#166534' }}>批次 {scannedLabel.batch_no} · 入庫後此標籤將標記為已使用</div>
                </div>
              </div>
            )}

            {/* 數量輸入 */}
            <div>
              <label style={st.label}>入庫數量 *{scannedLabel ? ' （自動帶入，可修改）' : ''}</label>
              <input
                style={{ ...st.input, fontSize: 28, fontWeight: 800, textAlign: 'center', padding: '16px', color: C.accent }}
                type="number" inputMode="numeric" min="1"
                value={qty} onChange={e => setQty(e.target.value)}
                placeholder="0"
                autoFocus
              />
              <div style={{ textAlign: 'center', fontSize: 12, color: C.muted, marginTop: 4 }}>單位：{scannedProduct?.unit}</div>
            </div>

            {/* 備註 */}
            <div>
              <label style={st.label}>備註（選填）</label>
              <input style={st.input} value={note} onChange={e => setNote(e.target.value)} placeholder="例：廠商送貨、生產完成..." />
            </div>

            {/* 確認按鈕 */}
            <button
              onClick={handleSubmit}
              disabled={saving || !qty || parseInt(qty) <= 0}
              style={{ ...st.btn(saving || !qty ? 'default' : 'success'), padding: '16px', fontSize: 16, fontWeight: 800, borderRadius: 14 }}>
              {saving ? '入庫中...' : `✓ 確認入庫 ${qty ? qty + ' ' + scannedProduct?.unit : ''}`}
            </button>
          </div>
        )}
      </div>
    </>
  )
}
